<script>
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import StatusBadge from '@/V2/components/status/StatusBadge.vue';
import CustomerListLoadingBars from '@/V2/customers/CustomerListLoadingBars.vue';
import {
  iButton,
  iButtonSizes,
  iButtonTypes,
  iButtonStyles,
  iKeyIcon,
  iKeyIconSize,
  iKeyIconShape,
  iKeyIconStyle,
  iKeyIconType,
} from '@inscribe/signal';
import {
  COMPLIANCE_CHECK_TYPE_TO_ICON,
  COMPLIANCE_RESULT_TO_READABLE_LABEL,
  COMPLIANCE_RESULT_TO_STATUS,
  CHECK_INFO,
  COMPLIANCE_ENTITY_TYPE,
} from '@/constants/businessComplianceAgent';
import { Statuses } from '@/V2/components/status/utils';

export default {
  components: {
    ExpandableContainer,
    iButton,
    iKeyIcon,
    ExpandableContainerItem,
    StatusBadge,
    CustomerListLoadingBars,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    latestComplianceRun: {
      type: Object,
      default: () => {},
    },
    isComplianceRunning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iButtonSizes,
      iButtonTypes,
      iButtonStyles,
      iKeyIconSize,
      iKeyIconShape,
      iKeyIconStyle,
      iKeyIconType,
      Statuses,
    };
  },
  computed: {
    individuals() {
      return this.latestComplianceRun?.output?.beneficial_owner_checks.map(
        (check) => {
          return {
            ...check,
            checks: check.checks.map((check) => {
              return {
                ...check,
                icon: COMPLIANCE_CHECK_TYPE_TO_ICON[check.type],
                badgeLabel: COMPLIANCE_RESULT_TO_READABLE_LABEL[check.result],
                status: COMPLIANCE_RESULT_TO_STATUS[check.result],
                subtype:
                  CHECK_INFO[COMPLIANCE_ENTITY_TYPE.INDIVIDUAL][check.type]
                    .subtype,
                hasBadge: true,
              };
            }),
          };
        },
      );
    },
    analysisHasRun() {
      return this.latestComplianceRun?.status === RUN_STATUS.COMPLETED;
    },
    showLoadingState() {
      return this.loading || this.isComplianceRunning;
    },
  },
  methods: {
    onViewProfileButtonClick() {
      this.$router.push({
        name: 'customer-profile',
        params: {
          customerId: this.$route.params.customerId,
        },
      });
    },
    onCheckClick(check) {
      this.$emit('show-check-details', check);
    },
    getSubtype(check) {
      const checkInfoForType =
        CHECK_INFO[COMPLIANCE_ENTITY_TYPE.INDIVIDUAL][check.type];
      if (checkInfoForType) {
        return checkInfoForType.subtypes[check.result] || '';
      } else {
        return 'Check completed';
      }
    },
  },
};
</script>

<template>
  <div
    class="compliance-individual-checks d-flex flex-column i-gap-8"
    data-testid="customer-compliance-individual-checks"
  >
    <ExpandableContainer
      v-for="individual in individuals"
      :key="individual.beneficial_owner_full_name"
      :title="
        isComplianceRunning
          ? 'Individual Checks'
          : individual.beneficial_owner_full_name
      "
      :subtitle="isComplianceRunning ? 'Analyzing...' : 'Individual Checks'"
    >
      <template #tools>
        <i-button
          label="View Profile"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.OUTLINE"
          @button-click="onViewProfileButtonClick"
        />
      </template>

      <template #items>
        <!-- loading state -->
        <customer-list-loading-bars
          v-if="showLoadingState"
          data-testid="customer-compliance-individual-checks-loading"
        >
        </customer-list-loading-bars>

        <template v-else>
          <expandable-container-item
            v-for="check in individual.checks"
            :key="check.type"
            :title="check.title"
            :subtitle="getSubtype(check)"
            aria-label="Expandable Container Item: Compliance Individual Check"
            data-dd-action-name="expandable-item-compliance-individual"
            @on-item-click="onCheckClick(check)"
            @on-right-icon-click="onCheckClick(check)"
            class="check-item"
            data-testid="customer-compliance-individual-check-item"
          >
            <template #left-container>
              <i-key-icon
                :icon="check.icon"
                :size="iKeyIconSize.MEDIUM"
                :shape="iKeyIconShape.SQUARE"
                :icon-style="iKeyIconStyle.OUTLINE"
                :type="iKeyIconType.NEUTRAL"
              />
            </template>

            <template #right-container>
              <status-badge
                v-if="check.hasBadge"
                :state="check.status"
                :label="check.badgeLabel"
              ></status-badge>
            </template>
          </expandable-container-item>
        </template>
      </template>
    </ExpandableContainer>
  </div>
</template>

<style lang="scss" scoped>
.compliance-individual-checks {
  :deep(.expanded) {
    gap: 1px;
  }
}
</style>
